import { useCallback, useEffect, useRef, useState } from "react";
import { riderApi } from "../services/riderApi";

const empty = { context: null, assignments: [], currentRun: null, status: "loading", stale: false, refreshing: false };

// One work snapshot per mounted workspace. Identity changes invalidate even
// responses from clients which fail to honour AbortSignal.
export default function useRiderWork(businessId, enabled = true, sessionKey = "") {
  const identity = `${enabled}:${businessId}:${sessionKey}`;
  const [state, setState] = useState({ ...empty, identity });
  const active = useRef(null);
  const delay = useRef(15000);
  const refresh = useCallback(async () => {
    if (!enabled || !businessId) return;
    active.current?.abort();
    const controller = new AbortController();
    active.current = controller;
    setState((previous) => ({ ...(previous.identity === identity ? previous : empty), identity, refreshing: true }));
    try {
      const context = await riderApi.getContext(businessId, { signal: controller.signal });
      const currentRun = await riderApi.getCurrentRun(businessId, { signal: controller.signal });
      const result = await riderApi.getAssignments(businessId, { signal: controller.signal });
      // Recent history is paginated. Recover current work outside that window
      // so a count never leads to an empty view just because newer history exists.
      const items = new Map((result?.items || []).map((item) => [item.id, item]));
      // WP9 slice 5: no offers and nothing accepted - committed work and work in
      // the Rider's hands are what remain.
      const counts = { COMMITTED: context.duty?.committedLoad, PICKED_UP: context.duty?.activeLoad };
      for (const [status, count] of Object.entries(counts)) {
        if (controller.signal.aborted) return;
        if (Number(count) <= [...items.values()].filter((item) => item.status === status).length || !count) continue;
        let cursor;
        do {
          const page = await riderApi.getAssignments(businessId, { status, cursor, signal: controller.signal });
          for (const item of page?.items || []) items.set(item.id, item);
          const next = page?.nextCursor;
          if (!page?.items?.length || !next || next === cursor) break;
          cursor = next;
        } while ([...items.values()].filter((item) => item.status === status).length < Number(count));
      }
      if (controller.signal.aborted) return;
      delay.current = 15000;
      setState({ identity, context, currentRun, assignments: [...items.values()], status: "ready", stale: false, refreshing: false });
    } catch (error) {
      if (controller.signal.aborted) return;
      const status = error?.status ?? error?.response?.status;
      const denied = status === 401 || status === 403;
      delay.current = Math.min(60000, delay.current * 2);
      setState((previous) => ({
        ...(denied || previous.identity !== identity ? empty : previous), identity,
        status: status === 401 ? "unauthorized" : status === 403 ? "ineligible" : "error",
        stale: true, refreshing: false,
      }));
    }
  }, [businessId, enabled, identity]);

  useEffect(() => {
    delay.current = 15000;
    let disposed = false;
    let timer;
    const schedule = () => {
      clearTimeout(timer);
      if (!disposed && enabled && businessId) timer = setTimeout(async () => {
        if (document.visibilityState === "visible") await refresh();
        schedule();
      }, delay.current);
    };
    const resume = () => {
      if (document.visibilityState !== "visible") return;
      clearTimeout(timer);
      refresh().then(schedule);
    };
    refresh().then(schedule);
    document.addEventListener("visibilitychange", resume);
    window.addEventListener("pageshow", resume);
    window.addEventListener("focus", resume);
    window.addEventListener("online", resume);
    return () => {
      disposed = true;
      clearTimeout(timer);
      active.current?.abort();
      document.removeEventListener("visibilitychange", resume);
      window.removeEventListener("pageshow", resume);
      window.removeEventListener("focus", resume);
      window.removeEventListener("online", resume);
    };
  }, [businessId, enabled, refresh]);

  return { ...(state.identity === identity ? state : empty), refreshContext: refresh };
}
